import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ServiceService } from '../../service.service';

interface Survey {
  survey_id: string;
  survey_number: string;
  survey_title: string;
  survey_description: string;
  status: string;
  created_date_time: string;
  total_assigned: number;
  total_completed: number;
  completion_percentage: string;
  created_by: {
    employee_id: string;
    employee_name: string;
  };
}

interface ApiResponse {
  success: boolean;
  message: string;
  TotalCount: number;
  currentPage: number;
  startRecord: number;
  endRecord: number;
  totalPages: number;
  data: Survey[];
}

interface DeleteResponse {
  success: boolean;
  message: string;
  deleted_count: number;
  data: any[];
}

@Component({
  selector: 'app-question-listing',
  templateUrl: './question-listing.component.html',
  styleUrls: ['./question-listing.component.css']
})
export class QuestionListingComponent implements OnInit {
  surveys: Survey[] = [];
  filteredSurveys: Survey[] = [];
  searchText: string = '';
  selectedStatus: string = 'ALL';
  isLoading: boolean = false;
  isDeleting: boolean = false;

  statusOptions: string[] = ['ALL', 'Pending', 'In Progress', 'Completed'];

  constructor(
    private router: Router,
    private service: ServiceService
  ) { }

  ngOnInit(): void {
    this.loadSurveys();
  }

  loadSurveys(): void {
    this.isLoading = true;

    this.service.viewCustomerSurveys('').subscribe({
      next: (response: ApiResponse) => {
        if (response.success) {
          this.surveys = response.data;
          this.filteredSurveys = response.data;
          this.applyFilters();
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading surveys:', error);
        this.isLoading = false;
      }
    });
  }

  onSearch(): void {
    this.applyFilters();
  }

  onStatusChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.surveys];

    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(survey =>
        survey.survey_title.toLowerCase().includes(search) ||
        survey.survey_number.toLowerCase().includes(search) ||
        survey.survey_description.toLowerCase().includes(search) ||
        survey.created_by.employee_name.toLowerCase().includes(search)
      );
    }

    if (this.selectedStatus !== 'ALL') {
      filtered = filtered.filter(survey =>
        survey.status === this.selectedStatus
      );
    }

    this.filteredSurveys = filtered;
  }

  onCreate(): void {
    this.router.navigate(['/admin/customer-survey-menu/create']);
  }

  onView(surveyId: string): void {
    this.router.navigate(['/admin/customer-survey-menu/detail', surveyId]);
  }

  onEdit(surveyId: string): void {
    this.router.navigate(['/admin/customer-survey-menu/edit', surveyId]);
  }

  onDelete(surveyId: string, surveyTitle: string): void {
    if (confirm(`Are you sure you want to delete "${surveyTitle}"?`)) {
      this.isDeleting = true;

      this.service.deleteCustomerSurvey([surveyId]).subscribe({
        next: (response: DeleteResponse) => {
          this.isDeleting = false;
          if (response.success) {
            this.service.showToast('success', response.message || 'Survey deleted successfully');
            this.loadSurveys();
          } else {
            this.service.showToast('error', 'Failed to delete survey: ' + response.message);
          }
        },
        error: (error: any) => {
          this.isDeleting = false;
          console.error('Error deleting survey:', error);
          this.service.showToast('error', 'An error occurred while deleting the survey. Please try again.');
        }
      });
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
        return 'status-completed';
      case 'In Progress':
        return 'status-in-progress';
      case 'Pending':
        return 'status-pending';
      default:
        return '';
    }
  }
}